/**
 * Drag & drop + browse-button upload target for video files.
 */
import React, { useCallback, useRef, useState } from 'react';

const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];

export default function DropZone({ onFileSelected }) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const validateAndEmit = useCallback(
    (file) => {
      if (!file) return;
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        setError(`Unsupported format ${ext}. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`);
        return;
      }
      setError('');
      onFileSelected(file);
    },
    [onFileSelected]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    validateAndEmit(file);
  };

  const handleBrowse = (e) => {
    const file = e.target.files?.[0];
    validateAndEmit(file);
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-xl p-14 text-center transition-all ${
          isDragging
            ? 'border-brand-600 bg-brand-50 scale-[1.01]'
            : 'border-gray-200 bg-gray-50/60 hover:border-brand-400 hover:bg-brand-50/30'
        }`}
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-brand-50 text-brand-600 grid place-items-center mb-5">
          <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
            <path
              d="M12 16V4m0 0 4 4m-4-4-4 4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-gray-800 font-medium">Drag &amp; drop your video here</p>
        <p className="text-gray-400 text-sm mt-1">or click to browse your files</p>
        <p className="text-gray-400 text-xs mt-4 tracking-wide">SUPPORTED: MP4 &middot; MOV &middot; AVI &middot; MKV &middot; WEBM</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          className="hidden"
          onChange={handleBrowse}
        />
      </div>
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
}
